<template>
  <div>
    <el-descriptions v-if="disabled" size="medium">
      <el-descriptions-item label="取消时间">
        {{ cancelDate }}
      </el-descriptions-item>
    </el-descriptions>
    <el-table
      ref="table"
      :data="pageTableData"
      border
      :row-key="new Date().toString()"
      :header-cell-style="{ background: '#f5f5f5' }"
    >
      <el-table-column
        label="序号"
        prop="index"
        align="center"
        type="index"
        width="60"
        fixed="left"
      />

      <el-table-column label="品目信息" min-width="340px" fixed="left">
        <template v-slot="{ row }">
          <div
            v-for="item in materielInfoConfig"
            :key="item.defineCode"
            class="info-item"
          >
            <span class="info-item-label">{{ item.columnName || '-' }}</span>
            <span
              v-if="item.columnName === '分类' && row.categoryNameTree"
              class="info-item-value"
            >
              {{ row.categoryNameTree.join('>') || '-' }}
            </span>
            <span v-else class="info-item-value">
              {{ row[item.defineCode] || '-' }}
            </span>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="采购量/单位" min-width="90px" fixed="left">
        <template v-slot="{ row }">
          <span>
            {{ row[numConfig.defineCode] }}/{{ row[unitConfig.defineCode] }}
          </span>
        </template>
      </el-table-column>

      <el-table-column
        :label="`预算单价(${isDollar ? '美元' : '元'})`"
        min-width="90px"
      >
        <template v-slot="{ row }">
          <span>{{ row[unitPriceConfig.defineCode] }}</span>
        </template>
      </el-table-column>

      <el-table-column
        :label="`预算总价(${isDollar ? '美元' : '元'})`"
        min-width="90px"
      >
        <template v-slot="{ row }">
          <span>{{ row.totalPrice }}</span>
          <money-in-cn v-if="!isDollar" :money="row.totalPrice || 0" />
        </template>
      </el-table-column>
      <el-table-column label="采购人指定" min-width="340px">
        <template v-slot="{ row }">
          <div
            v-for="item in purchaseSpecifyConfig"
            :key="item.defineCode"
            class="info-item"
          >
            <span class="info-item-label">{{ item.columnName || '-' }}</span>
            <span class="info-item-value">
              {{ row[item.defineCode] || '-' }}
            </span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="报价供应商数量" min-width="120px">
        <template v-slot="{ row }">
          <span>{{ getMaterialQuoteSupplierNum(row) }}</span>
        </template>
      </el-table-column>
      <el-table-column v-if="!disabled" label="操作">
        <template v-slot="{ row }">
          <el-button type="text" @click="$emit('cancelFlow', row)">
            退回授标
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!--前端模拟分页-->
    <pagination
      v-show="tableDataTemp.length > size"
      :total="tableDataTemp.length"
      size="small"
      :page.sync="page"
      :limit.sync="size"
    />

    <el-form
      :model="formData"
      label-width="110px"
      label-suffix="："
      class="m-t-20 detail-content-width"
    >
      <el-form-item label="取消采购原因">
        <span v-if="disabled">{{ formData.cancelReason || '-' }}</span>

        <template v-else>
          <el-select v-model="cancelReasonValue">
            <el-option
              v-for="item in materialFlowReasonDict.array"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <el-input
            v-if="cancelReasonValue === materialFlowReasonDict.OTHER.value"
            v-model="formData.cancelReason"
            type="textarea"
            :rows="4"
            :maxlength="500"
            show-word-limit
            class="m-t-10"
          />
        </template>
      </el-form-item>

      <el-form-item label="说明文件">
        <upload
          :file-id-before.sync="formData.cancelAttachmentIds"
          :props-data="{
            uploadMode: 'fileId',
            limit: 10,
            disabled: disabled
          }"
        />
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
// 组件
import Upload from '@/components/common/Upload'
import Pagination from '@/components/common/Pagination'
import MoneyInCn from '@/components/common/MoneyInCn'

// Mixin
import materialColumnMixin from '@/components/common/PurchaseListComponent/material-column-mixin'

// 数据字典
import { materialFlowReasonDict } from '@/dictionary/project/common'

// 工具函数
import { uniqBy } from 'lodash'

export default {
  name: 'FlowItemSheet',

  components: { Upload, Pagination, MoneyInCn },

  mixins: [materialColumnMixin],

  props: {
    tableData: {
      type: Array,
      default: () => []
    },

    // 外部传入的模板配置
    itemSchemaList: {
      type: Array,
      default: () => []
    },

    // 是否为美元报价
    isDollar: {
      type: Boolean,
      default: false
    },

    // 取消采购原因的对象，sync
    reasonForm: {
      type: Object,
      default: () => {}
    },

    disabled: {
      type: Boolean,
      default: false
    },

    // 取消时间
    cancelDate: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      // 字典
      materialFlowReasonDict,

      // 前端模拟分页
      page: 1,
      size: 20,
      pageTableData: [],

      formData: {
        cancelReason: null,
        cancelAttachmentIds: []
      }
    }
  },

  computed: {
    // 对传入的品目进行去重
    tableDataTemp() {
      return uniqBy(this.tableData, 'materielId')
    },

    // 退货原因的value，用于这只formData的cancelReason
    cancelReasonValue: {
      get() {
        if (this.formData.cancelReason === null) return null

        const index = materialFlowReasonDict.array.findIndex(
          dict => dict.label === this.formData.cancelReason
        )
        if (index >= 0) {
          return materialFlowReasonDict.array[index]?.value
        } else {
          return materialFlowReasonDict.OTHER.value
        }
      },
      set(val) {
        if (val !== materialFlowReasonDict.OTHER.value) {
          this.formData.cancelReason =
            materialFlowReasonDict.translate(val)?.label || ''
        } else {
          this.formData.cancelReason = ''
        }
      }
    }
  },

  watch: {
    // 监听传入的品目模板
    itemSchemaList: {
      handler(val) {
        if (val.length > 0) {
          this.setColumnConfig(val)
        }
      },
      deep: true,
      immediate: true
    },

    // 监听传入的品目数据
    tableData: {
      handler(val) {
        this.getPageData()
      },
      deep: true,
      immediate: true
    },

    size() {
      this.getPageData()
    },

    page() {
      this.getPageData()
    },

    // 通过监听reasonForm和formData实现双向绑定
    reasonForm: {
      handler: function(val) {
        this.formData = Object.assign({}, this.formData, val)
      },
      immediate: true
    },
    formData: {
      handler: function(val) {
        if (JSON.stringify(this.reasonForm) === JSON.stringify(val)) return

        this.$emit('update:reasonForm', val)
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    /**
     * 获取品目报价供应商数量
     * @param row
     * @returns {number|number|*}
     */
    getMaterialQuoteSupplierNum(row) {
      if ('supplierNum' in row) return row.supplierNum || 0

      return (
        Object.keys(row.offerValue || {}).filter(key => {
          const value = row.offerValue[key]
          return value.tradeId
        }).length || 0
      )
    },

    /**
     * 前端模拟分页：获取分页数据
     */
    getPageData() {
      const start = (this.page - 1) * this.size
      const end = start + this.size
      this.pageTableData = this.tableDataTemp.slice(start, end)
    }
  }
}
</script>

<style scoped lang="scss">
.info-item {
  display: flex;
  align-items: stretch;
  margin: 24px 0;
  line-height: 22px;

  &:first-child {
    margin-top: 16px;
  }
  &:last-child {
    margin-bottom: 16px;
  }

  .info-item-label {
    color: $text-color;
    width: 40%;
    margin-right: 24px;
    font-weight: bold;
  }
  .info-item-value {
    flex: 1;
    color: $second-text-color;
  }
}

.supplier-response {
  ::v-deep .el-checkbox__label {
    font-size: 12px;
  }
}
</style>
